import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { MARKET } from "@/lib/market";

interface CompanyPrice {
  product_id: string;
  product_type: "equipment" | "consumable";
  custom_price: number;
  discount_percentage: number;
}

export function useCompanyPricing() {
  const { user } = useAuth();

  const { data: companyPrices, isLoading } = useQuery({
    queryKey: ["company-prices", MARKET, user?.id],
    queryFn: async () => {
      if (!user) return [];

      // Get user's company_id from profile
      const { data: profile } = await supabase
        .from("profiles")
        .select("company_id")
        .eq("id", user.id)
        .maybeSingle();

      if (!profile?.company_id) return [];

      const { data, error } = await supabase
        .from("company_prices")
        .select("product_id, product_type, custom_price, discount_percentage, companies!inner(market)")
        .eq("companies.market", MARKET)
        .eq("company_id", profile.company_id);

      if (error) throw error;
      return data as CompanyPrice[];
    },
    enabled: !!user,
  });

  // Get custom price for a product (consumable or equipment)
  const getProductPrice = useCallback((
    productId: string,
    productType: "equipment" | "consumable",
    defaultPrice: number
  ): { price: number; hasDiscount: boolean; discountPercentage: number } => {
    if (!companyPrices || companyPrices.length === 0) {
      return { price: defaultPrice, hasDiscount: false, discountPercentage: 0 };
    }

    const customPrice = companyPrices.find(
      (p) => p.product_id === productId && p.product_type === productType
    );

    if (customPrice) {
      return {
        price: customPrice.custom_price,
        hasDiscount: customPrice.discount_percentage > 0,
        discountPercentage: customPrice.discount_percentage,
      };
    }

    return { price: defaultPrice, hasDiscount: false, discountPercentage: 0 };
  }, [companyPrices]);

  // Get custom price for a variant - variants are stored with their own ID
  const getVariantPrice = useCallback((
    variantId: string,
    defaultPrice: number
  ): { price: number; hasDiscount: boolean; discountPercentage: number; originalPrice: number } => {
    if (!companyPrices || companyPrices.length === 0) {
      return { price: defaultPrice, hasDiscount: false, discountPercentage: 0, originalPrice: defaultPrice };
    }

    // Variants are stored as product_type: 'consumable' with the variant's ID as product_id
    const customPrice = companyPrices.find(
      (p) => p.product_id === variantId
    );

    if (customPrice) {
      return {
        price: customPrice.custom_price,
        hasDiscount: customPrice.discount_percentage > 0,
        discountPercentage: customPrice.discount_percentage,
        originalPrice: defaultPrice,
      };
    }

    return { price: defaultPrice, hasDiscount: false, discountPercentage: 0, originalPrice: defaultPrice };
  }, [companyPrices]);

  return {
    companyPrices,
    isLoading,
    getProductPrice,
    getVariantPrice,
    hasCustomPricing: !!companyPrices && companyPrices.length > 0,
  };
}
